import { Flex, Form, Switch, Tag, Typography } from "antd"
import React, { memo } from "react"

import type {
  ActionRecord,
  OnFeaturesToggle,
  PermissionsMode,
  SectionRecord
} from "../types.ts"
import type {
  PermissionDisplayMap,
  PermissionFeatures
} from "../types/permissions.types.ts"
import { PermissionsFeatureRow } from "./PermissionsFeatureRow.tsx"

const { Text } = Typography

type PermissionsFeaturesBlockProps = {
  parentKey: keyof PermissionFeatures
  features: ActionRecord
  onFeaturesToggle?: OnFeaturesToggle
  isUpdating?: boolean
  disabled?: boolean
  displayMap?: PermissionDisplayMap | null
  modeProps: PermissionsMode
}

export const PermissionsFeaturesBlock = memo(
  ({
    parentKey,
    features,
    onFeaturesToggle,
    isUpdating,
    disabled,
    displayMap,
    modeProps
  }: PermissionsFeaturesBlockProps) => {
    const watchedSection = Form.useWatch<SectionRecord | undefined>(parentKey)
    const watchedFeatures = watchedSection?.["features"] as
      | ActionRecord
      | undefined
    const effectiveFeatures = (watchedFeatures ?? features) as ActionRecord
    const allFeaturesEnabled =
      Object.keys(features).length > 0 &&
      Object.keys(features).every((k) => effectiveFeatures?.[k] === true)

    return (
      <div className={"mt-4"}>
        <Flex
          align="center"
          justify="space-between"
          className={"px-4 py-2 bg-background border-b border-border"}
        >
          <Flex align="center" gap={8}>
            <Text type="secondary" className={"uppercase"}>
              Features
            </Text>
            <Tag>
              {Object.values(effectiveFeatures).filter(Boolean).length}/
              {Object.values(features).length}
            </Tag>
          </Flex>
          {onFeaturesToggle && (
            <Flex
              justify="center"
              align="center"
              gap={6}
              className="px-2 -mr-2 py-1 rounded border border-border shadow-sm"
            >
              <Text
                type="secondary"
                className="text-xs uppercase tracking-[0.04em]"
              >
                Toggle all
              </Text>
              <Switch
                size="small"
                checked={allFeaturesEnabled}
                onChange={(v) => onFeaturesToggle(parentKey, features, v)}
                disabled={isUpdating || disabled}
              />
            </Flex>
          )}
        </Flex>

        <div
          className={"gap-0 grid"}
          style={{ gridTemplateColumns: "1fr 1fr" }}
        >
          {Object.entries(features).map(([featureKey, value], idx) => (
            <PermissionsFeatureRow
              key={featureKey}
              parentKey={parentKey}
              featureKey={featureKey}
              value={value}
              idx={idx}
              isUpdating={isUpdating}
              displayMap={displayMap}
              {...modeProps}
            />
          ))}
        </div>
      </div>
    )
  }
)

PermissionsFeaturesBlock.displayName = "PermissionsFeaturesBlock"
